// ** React imports
import React, { memo, useCallback, useMemo } from 'react';

// ** Third Party imports
import { useDispatch } from 'react-redux';
import { Button } from '@saleshandy/design-system';
import Modal from '../../../../design-system/components/atoms/modal';
import { Placement } from '../../../../design-system/components/overlay';
import Select from '../../../../design-system/components/select';

import { setShowMicrophoneAccessWarningModal } from '../../../../../components/dialer/dialer-slice';

// ─── Inline SVG Icons ────────────────────────────────────────────────────────

const SpeakerIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11 5L6 9H2v6h4l5 4V5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M19.07 4.93a10 10 0 0 1 0 14.14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.54 8.46a5 5 0 0 1 0 7.07"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MicIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="9"
      y="2"
      width="6"
      height="11"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 10a7 7 0 0 0 14 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="19"
      x2="12"
      y2="23"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line
      x1="8"
      y1="23"
      x2="16"
      y2="23"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const WifiIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 12.55a11 11 0 0 1 14.08 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.42 9a16 16 0 0 1 21.16 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.53 16.11a6 6 0 0 1 6.95 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="20" r="1" fill="currentColor" />
  </svg>
);

// ─── IntensityBars Sub-component ─────────────────────────────────────────────

interface IntensityBarsProps {
  level: number;
  color?: 'gray' | 'green' | 'yellow' | 'red';
  animate?: boolean;
}

const IntensityBars: React.FC<IntensityBarsProps> = ({
  level,
  color = 'green',
  animate = false,
}) => {
  const bars = Array.from({ length: 10 }, (_, i) => {
    const filled = i < level;
    let barColor = 'gray';
    if (filled) {
      barColor = color;
    }
    const isAnimating = animate && filled && level > 0;
    return (
      <div
        key={i}
        className={[
          'pre-call-test-modal-bar',
          filled ? `pre-call-test-modal-bar--${barColor}` : '',
          isAnimating ? 'pre-call-test-modal-bar--animate' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      />
    );
  });

  return <div className="pre-call-test-modal-bars">{bars}</div>;
};

// ─── Props Interface ──────────────────────────────────────────────────────────

interface DeviceOption {
  key: string;
  label: string;
}

interface PreCallTestModalProps {
  show: boolean;
  onClose: () => void;
  onDone: () => void;

  // Speaker
  speakerDevices?: DeviceOption[];
  selectedSpeakerKey?: string;
  onSpeakerChange?: (key: string) => void;
  onTestSpeaker?: () => void;
  onStopSpeakerTest?: () => void;
  speakerLevel?: number;
  isSpeakerTesting?: boolean;
  speakerError?: string;

  // Microphone
  micDevices?: DeviceOption[];
  selectedMicKey?: string;
  onMicChange?: (key: string) => void;
  onTestMic?: () => void;
  onStopMicTest?: () => void;
  micLevel?: number;
  isMicTesting?: boolean;
  micError?: string;

  // Network
  networkLevel?: number;
  networkStatus?: string;

  // Loading
  areDevicesLoading?: boolean;

  // Permission
  hasMicPermission?: boolean;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const PreCallTestModal: React.FC<PreCallTestModalProps> = ({
  show,
  onClose,
  onDone,

  speakerDevices = [],
  selectedSpeakerKey,
  onSpeakerChange,
  onTestSpeaker,
  onStopSpeakerTest,
  speakerLevel = 0,
  isSpeakerTesting = false,
  speakerError,

  micDevices = [],
  selectedMicKey,
  onMicChange,
  onTestMic,
  onStopMicTest,
  micLevel = 0,
  isMicTesting = false,
  micError,

  networkLevel = 0,
  networkStatus = 'Checking...',

  areDevicesLoading = false,

  hasMicPermission = true,
}) => {
  const dispatch = useDispatch();

  const speakerOptions = useMemo(
    () =>
      speakerDevices?.map((d) => ({
        key: d.key,
        label: d.label,
        value: d.key,
      })),
    [JSON.stringify(speakerDevices)],
  );

  const micOptions = useMemo(
    () =>
      micDevices?.map((d) => ({
        key: d.key,
        label: d.label,
        value: d.key,
      })),
    [JSON.stringify(micDevices)],
  );

  const isSpeakerEmpty = useMemo(() => speakerOptions?.length === 0, [
    speakerOptions,
  ]);
  const isMicEmpty = useMemo(() => micOptions?.length === 0, [micOptions]);

  const handleSpeakerTestClick = useCallback(() => {
    if (!hasMicPermission) {
      dispatch(setShowMicrophoneAccessWarningModal(true));
      return;
    }
    if (isSpeakerTesting) {
      onStopSpeakerTest?.();
    } else {
      onTestSpeaker?.();
    }
  }, [hasMicPermission, isSpeakerTesting, onStopSpeakerTest, onTestSpeaker]);

  const handleMicTestClick = useCallback(() => {
    if (!hasMicPermission) {
      dispatch(setShowMicrophoneAccessWarningModal(true));
      return;
    }
    if (isMicTesting) {
      onStopMicTest?.();
    } else {
      onTestMic?.();
    }
  }, [hasMicPermission, isMicTesting, onStopMicTest, onTestMic]);

  const networkBarColor = useMemo<'green' | 'yellow' | 'red'>(() => {
    if (networkLevel >= 7) return 'green';
    if (networkLevel >= 4) return 'yellow';
    return 'red';
  }, [networkLevel]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      onHide={onClose}
      backdrop="static"
      titleContent="Pre-call Test"
      showCloseIcon
      className="pre-call-test-modal"
      hideFooter
    >
      {/* Body */}
      <div className="pre-call-test-modal-body">
        {/* Speaker Section */}
        <div className="pre-call-test-modal-section">
          <div className="pre-call-test-modal-section-label">
            <SpeakerIcon />
            Speaker
          </div>

          <div className="pre-call-test-modal-row">
            <div className="pre-call-test-modal-select-wrapper">
              <Select
                className="pre-call-test-modal-select"
                options={speakerOptions}
                selectedOptionKey={selectedSpeakerKey}
                optionRenderer={(option) => <span>{option.label}</span>}
                selectedOptionRenderer={([option]) => (
                  <span>{option?.label}</span>
                )}
                onChange={([option]) => {
                  if (option?.key) onSpeakerChange?.(option.key);
                }}
                placement={Placement.Bottom}
                disabled={isSpeakerEmpty || areDevicesLoading}
                {...(isSpeakerEmpty && {
                  placeholder: areDevicesLoading
                    ? 'Loading speaker devices...'
                    : 'No speaker device found',
                })}
              />
            </div>

            <button
              type="button"
              className="pre-call-test-modal-test-btn"
              onClick={handleSpeakerTestClick}
            >
              {isSpeakerTesting ? 'Stop' : 'Test Speaker'}
            </button>
          </div>

          <div className="pre-call-test-modal-intensity-row-container">
            <div className="pre-call-test-modal-intensity-row">
              <label>Intensity:</label>
              <IntensityBars
                level={speakerLevel}
                color="green"
                animate={isSpeakerTesting}
              />
            </div>
            {speakerError && (
              <div className="pre-call-test-modal-error">{speakerError}</div>
            )}
          </div>
        </div>

        {/* Microphone Section */}
        <div className="pre-call-test-modal-section">
          <div className="pre-call-test-modal-section-label">
            <MicIcon />
            Microphone
          </div>

          <div className="pre-call-test-modal-row">
            <div className="pre-call-test-modal-select-wrapper">
              <Select
                className="pre-call-test-modal-select"
                options={micOptions}
                selectedOptionKey={selectedMicKey}
                optionRenderer={(option) => <span>{option.label}</span>}
                selectedOptionRenderer={([option]) => (
                  <span>{option?.label}</span>
                )}
                onChange={([option]) => {
                  if (option?.key) onMicChange?.(option.key);
                }}
                placement={Placement.Bottom}
                disabled={isMicEmpty || areDevicesLoading}
                {...(isMicEmpty && {
                  placeholder: areDevicesLoading
                    ? 'Loading microphone devices...'
                    : 'No microphone device found',
                })}
              />
            </div>

            <button
              type="button"
              className="pre-call-test-modal-test-btn"
              onClick={handleMicTestClick}
            >
              {isMicTesting ? 'Stop' : 'Test Mic'}
            </button>
          </div>

          <div className="pre-call-test-modal-intensity-row-container">
            <div className="pre-call-test-modal-intensity-row">
              <label>Intensity:</label>
              <IntensityBars
                level={micLevel}
                color="green"
                animate={isMicTesting}
              />
            </div>
            {micError && (
              <div className="pre-call-test-modal-error">{micError}</div>
            )}
          </div>
        </div>

        {/* Network Strength Section */}
        <div className="pre-call-test-modal-section">
          <div className="pre-call-test-modal-section-label">
            <WifiIcon />
            Network Strength
          </div>

          <div className="pre-call-test-modal-intensity-row">
            <label>{networkStatus || 'No network'}</label>
            <IntensityBars level={networkLevel} color={networkBarColor} />
          </div>
        </div>
      </div>

      {/* Custom Footer */}
      <div className="pre-call-test-modal-footer">
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
    </Modal>
  );
};

export default memo(PreCallTestModal);
